(() => {
  if (customElements.get('wheel-configurator')) return;
  const fields = ['size', 'offset', 'pcd', 'color'];
  const unique = (values) => [...new Set(values)];

  class WheelConfigurator extends HTMLElement {
    static parseConfiguration(value) {
      const match = /^(\d+(?:,\d+)?)×(\d+(?:,\d+)?)J \| ET(-?\d+(?:,\d+)?) \| (\d+×\d+(?:,\d+)?) \| CB(\d+(?:,\d+)?)$/.exec(value);
      if (!match) throw new Error('Invalid wheel configuration');
      return { value, size: `${match[1]}″ × ${match[2]}J`, offset: `ET${match[3]}`, pcd: match[4], cb: match[5] };
    }

    // The source matrix contains only actual configurations; never combine independent dimensions.
    static resolve(rows, selected, changedField) {
      const limit = fields.indexOf(changedField);
      let candidates = rows;
      for (let index = 0; index < 3; index++) {
        const field = fields[index];
        const values = unique(candidates.map((row) => row[field]));
        if (!values.includes(selected[field])) {
          if (index <= limit) throw new Error('Invalid choice');
          selected[field] = values[0];
        }
        candidates = candidates.filter((row) => row[field] === selected[field]);
      }
      if (candidates.length !== 1) throw new Error('Ambiguous wheel geometry');
      const row = candidates[0];
      if (!row.colors.includes(selected.color)) selected.color = row.colors[0];
      return { row, selected };
    }

    connectedCallback() {
      this.info = this.closest('product-info');
      this.onChange = this.handleChange.bind(this);
      this.onRequest = this.handleRequest.bind(this);
      this.onSubmit = (event) => {
        if (!this.busy) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      };
      this.addEventListener('change', this.onChange);
      this.addEventListener('wheel:request', this.onRequest);
      this.info?.addEventListener('submit', this.onSubmit, true);
      this.mounted = true;
      Promise.all([customElements.whenDefined('product-info'), customElements.whenDefined('variant-selects')])
        .then(() => { if (this.mounted) this.initialize(); });
    }

    disconnectedCallback() {
      this.mounted = false;
      clearTimeout(this.requestTimeout);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('wheel:request', this.onRequest);
      // Removing only this component must not unlock the old cart ID underneath pending choices.
      if (this.busy) {
        this.info?.abortController?.abort();
        if (this.info) this.info.pendingRequestUrl = null;
        if (this.rollbackPendingSelection()) this.restorePurchaseControls();
        else this.blockUnconfirmedPurchase();
      } else {
        this.restorePurchaseControls();
      }
      this.info?.removeEventListener('submit', this.onSubmit, true);
      if (this.native) this.native.hidden = false;
    }

    initialize() {
      try {
        if (!this.info) throw new Error('Missing product section');
        this.catalog = JSON.parse(this.querySelector('[data-wheel-catalog]').textContent);
        if (!this.catalog?.configurations?.length || !this.catalog.colors?.length) throw new Error('Missing wheel matrix');
        this.rows = this.catalog.configurations.map(({ value, colors }) => {
          if (!Array.isArray(colors) || !colors.length || unique(colors).length !== colors.length) throw new Error('Invalid colors');
          return { ...WheelConfigurator.parseConfiguration(value), colors };
        });
        const keys = this.rows.map(({ size, offset, pcd }) => JSON.stringify([size, offset, pcd]));
        if (unique(keys).length !== keys.length) throw new Error('Ambiguous center bore');
        this.controls = Object.fromEntries(fields.map((field) => [field, this.querySelector(`[data-wheel-field="${field}"]`)]));
        if (Object.values(this.controls).some((control) => !control)) throw new Error('Missing controls');
        this.syncFromNative();
        this.enhanced = true;
        this.setAttribute('data-wheel-active', '');
        this.hidden = false;
        this.native.hidden = true;
      } catch (_) {
        this.fallback(false);
      }
    }

    findNative() {
      // The theme inserts the replacement before the old picker during its view transition.
      const native = this.info.querySelector('variant-selects[data-wheel-native]');
      const selects = [...(native?.querySelectorAll('select[data-wheel-position]') || [])];
      if (selects.length !== 2) throw new Error('Missing native option values');
      for (const [index, expected] of [this.rows.map((row) => row.value), this.catalog.colors].entries()) {
        const options = [...selects[index].options];
        if (options.length !== expected.length || unique(options.map((option) => option.value)).length !== expected.length) throw new Error('Changed wheel options');
        if (options.some((option) => !expected.includes(option.value) || !/^\d+$/.test(option.dataset.optionValueId || ''))) throw new Error('Invalid native option');
      }
      this.native = native;
      this.nativeSelects = selects;
    }

    syncFromNative() {
      this.findNative();
      const selectedVariant = JSON.parse(this.native.querySelector('[data-selected-variant]').textContent);
      const row = this.rows.find((candidate) => candidate.value === this.nativeSelects[0].value);
      const color = this.nativeSelects[1].value;
      if (!selectedVariant?.id || !row?.colors.includes(color)) throw new Error('Unknown selected variant');
      if (selectedVariant.options?.[0] !== row.value || selectedVariant.options?.[1] !== color) throw new Error('Variant does not match selection');
      this.selected = { size: row.size, offset: row.offset, pcd: row.pcd, color };
      this.renderControls(row);
      // Capture only a server-confirmed state; pending native option values are not confirmation.
      const section = CSS.escape(this.info.dataset.section);
      this.confirmed = {
        variant: { ...selectedVariant, options: [...selectedVariant.options] },
        sku: this.info.querySelector(`#Sku-${section}`)?.innerHTML ?? null,
        price: this.info.querySelector(`#price-${section}`)?.innerHTML ?? null,
      };
      return selectedVariant;
    }

    renderControls(row) {
      let candidates = this.rows;
      for (const field of fields) {
        const values = field === 'color' ? row.colors : unique(candidates.map((candidate) => candidate[field]));
        const select = this.controls[field];
        select.replaceChildren(...values.map((value) => new Option(value, value, false, value === this.selected[field])));
        if (field !== 'color') candidates = candidates.filter((candidate) => candidate[field] === this.selected[field]);
      }
      this.querySelector('[data-wheel-summary]').textContent = `${row.size} · ${row.offset} · ${row.pcd} · ${this.dataset.centerBoreLabel}: ${row.cb} mm · ${this.selected.color}`;
    }

    handleChange(event) {
      const field = event.target.dataset.wheelField;
      if (!fields.includes(field)) return;
      event.stopPropagation();
      try {
        this.findNative();
        const next = { ...this.selected, [field]: event.target.value };
        const { row, selected } = WheelConfigurator.resolve(this.rows, next, field);
        this.selected = selected;
        this.renderControls(row);
        this.focusField = field;
        // Update both actual options before emitting ONE native change event.
        for (const [index, value] of [row.value, selected.color].entries()) {
          for (const option of this.nativeSelects[index].options) {
            // This theme tests getAttribute('selected') for truthiness: an empty boolean attribute fails.
            if (option.value === value) option.setAttribute('selected', 'selected');
            else option.removeAttribute('selected');
          }
          this.nativeSelects[index].value = value;
        }
        this.nativeSelects[1].dispatchEvent(new Event('change', { bubbles: true }));
        this.controls[field].focus();
      } catch (_) {
        this.lockPurchaseControls();
        this.fallback(true);
      }
    }

    rollbackPendingSelection() {
      try {
        this.findNative();
        const confirmed = this.confirmed;
        const variant = JSON.parse(this.native.querySelector('[data-selected-variant]').textContent);
        const section = CSS.escape(this.info.dataset.section);
        const inputs = [...this.info.querySelectorAll(
          `#product-form-${section} input[name="id"], #product-form-installment-${section} input[name="id"]`
        )];
        if (!confirmed || !inputs.length || inputs.some((input) => input.value !== String(confirmed.variant.id))) return false;
        if (variant?.id !== confirmed.variant.id || variant?.sku !== confirmed.variant.sku ||
            confirmed.variant.options.some((value, index) => value !== variant.options?.[index])) return false;
        // Do not roll back just the labels if a response partially changed the form, price or SKU.
        if ((this.info.querySelector(`#Sku-${section}`)?.innerHTML ?? null) !== confirmed.sku ||
            (this.info.querySelector(`#price-${section}`)?.innerHTML ?? null) !== confirmed.price) return false;
        if (confirmed.variant.options.some((value, index) => ![...this.nativeSelects[index].options].some((option) => option.value === value))) return false;
        for (const [index, value] of confirmed.variant.options.entries()) {
          for (const option of this.nativeSelects[index].options) {
            if (option.value === value) option.setAttribute('selected', 'selected');
            else option.removeAttribute('selected');
          }
          this.nativeSelects[index].value = value;
        }
        this.info.productForm?.toggleSubmitButton(!confirmed.variant.available, window.variantStrings.soldOut);
        return true;
      } catch (_) {
        return false;
      }
    }

    blockUnconfirmedPurchase() {
      // A partially applied response cannot be made safe by restoring only native option labels.
      // Leave inert in place and clear cart IDs so removing the component cannot submit another SKU.
      this.info?.productForm?.toggleSubmitButton(true);
      this.info?.querySelectorAll('form[id^="product-form-"] input[name="id"]').forEach((input) => {
        input.value = '';
        input.disabled = true;
      });
    }

    lockPurchaseControls() {
      this.busy = true;
      this.info.setAttribute('data-wheel-loading', '');
      if (this.purchaseControls) return;
      this.purchaseControls = [...this.info.querySelectorAll('product-form, .shopify-payment-button, form[id^="product-form-installment-"]')]
        .map((element) => ({ element, inert: element.inert }));
      this.purchaseControls.forEach(({ element }) => { element.inert = true; });
    }

    restorePurchaseControls() {
      this.purchaseControls?.forEach(({ element, inert }) => { element.inert = inert; });
      this.purchaseControls = null;
      this.busy = false;
      this.info?.removeAttribute('data-wheel-loading');
    }

    handleRequest({ detail: { state } }) {
      clearTimeout(this.requestTimeout);
      const status = this.querySelector('[data-wheel-status]');
      if (state === 'loading') {
        this.findNative();
        this.expectedSelection = this.nativeSelects.map((select) => select.value);
        this.lockPurchaseControls();
        this.setAttribute('aria-busy', 'true');
        status.textContent = this.dataset.loadingLabel;
        status.hidden = false;
        this.requestTimeout = setTimeout(() => {
          this.info.abortController?.abort();
          this.fallback(true);
        }, 15000);
      } else if (state === 'complete') {
        try {
          this.findNative();
          if (this.expectedSelection?.some((value, index) => value !== this.nativeSelects[index].value)) throw new Error('Unexpected variant response');
          this.syncFromNative();
          if (this.enhanced) this.native.hidden = true;
          this.restorePurchaseControls();
          this.removeAttribute('aria-busy');
          status.hidden = true;
          if (this.enhanced && this.focusField) this.controls[this.focusField].focus();
        } catch (_) {
          this.lockPurchaseControls();
          this.fallback(true);
        }
      } else if (state === 'error') {
        this.fallback(true);
      }
    }

    fallback(showError) {
      this.enhanced = false;
      this.removeAttribute('aria-busy');
      if (this.native) this.native.hidden = false;
      else this.info?.querySelector('variant-selects[data-wheel-native]')?.removeAttribute('hidden');
      // Reveal usable native controls. Keep cart locked after a failed request until a valid response.
      this.querySelectorAll('.product-form__input, [data-wheel-summary]').forEach((element) => { element.hidden = true; });
      const status = this.querySelector('[data-wheel-status]');
      if (status) {
        status.textContent = this.dataset.errorLabel || '';
        status.hidden = !showError;
      }
      this.hidden = !showError;
    }
  }
  customElements.define('wheel-configurator', WheelConfigurator);
})();
